import React from 'react';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Tag from '@amzn/meridian/tag';
import Box from '@amzn/meridian/box';
import Divider from '@amzn/meridian/divider';
import cronstrue from 'cronstrue';
import { useTranslation } from 'src/arb-wrapper';
import { CampaignStatuses } from 'src/store/constants';
import { FEATURE_FLAGS } from 'src/constants';
import { type CampaignDesiredStateDto, type CampaignConfigurationDto, type ProgramDto, type CampaignConfigurationControllerFindManyApiResponse } from 'src/store/generated/leela-api-generated';
import { DateTime } from 'luxon';
import { isFeatureEnabled } from 'src/feature-flags';
import { PublisherDisplay } from 'src/components/common/PublisherDisplay';
import { ProductStrategies } from 'src/components/campaigns/ProductStrategies';

interface CampaignDetailsViewProps {
  campaign: CampaignDesiredStateDto;
  program?: ProgramDto;
  blocklistConfigs?: CampaignConfigurationControllerFindManyApiResponse;
  contentRuleConfigs?: CampaignConfigurationControllerFindManyApiResponse;
}

/**
 * Information-section row listing the content rules attached to a campaign as a comma-separated
 * string of rule names. The caller gates rendering on the asset-eval feature flag and omits the
 * row entirely when the campaign has no attached rules.
 */
const CampaignContentRulesRow: React.FC<{ contentRuleNames: string }> = ({ contentRuleNames }) => {
  const { t } = useTranslation();
  return (
    <Row alignmentVertical="top" spacing="200" widths={['240px', 'fill']}>
      <Text color="secondary" type="b300">{t('form_campaigns_content_rules')}</Text>
      <Text data-testid="campaign-details-content-rules" type="b300">{contentRuleNames}</Text>
    </Row>
  );
};

export const CampaignDetailsView: React.FC<CampaignDetailsViewProps> = ({ campaign, program, blocklistConfigs, contentRuleConfigs }) => {
  const { t } = useTranslation();

  const blocklistNames = blocklistConfigs?.data
    ?.filter((config: CampaignConfigurationDto) => campaign.configIds?.includes(config.id))
    .map((config: CampaignConfigurationDto) => config.name ?? config.id)
    .join(', ');

  // Comma-separated names of the content rules attached to this campaign. Shown only when
  // the campaign has at least one rule attached (row is omitted otherwise).
  const contentRuleNames = contentRuleConfigs?.data
    ?.filter((config: CampaignConfigurationDto) => campaign.configIds?.includes(config.id))
    .map((config: CampaignConfigurationDto) => config.name ?? config.id)
    .join(', ');

  const formatDate = (dateValue: string | object | undefined) => {
    if (!dateValue) return 'N/A';

    let dateString: string;
    if (typeof dateValue === 'string') {
      dateString = dateValue;
    } else if (typeof dateValue === 'object' && '$date' in dateValue) {
      dateString = (dateValue as { $date: string }).$date;
    } else {
      return 'N/A';
    }

    const dateTime = DateTime.fromISO(dateString).toUTC();
    return dateTime.toLocaleString(DateTime.DATETIME_FULL);
  };

  return (
    <div style={{ width: '100%', margin: '0 auto' }}>
      <Box spacingInset="500" type="outline">
        <Column spacing="600">
          {/* Basic Information */}
          <Column spacing="400">
            <Text type="h200">{t('information_header')}</Text>
            <Divider />

            <Column spacing="300">
              <Row spacing="200" widths={['240px', 'fill']}>
                <Text color="secondary" type="b300">{t('form_global_name')}</Text>
                <Column spacing="100">
                  <Text type="b300">{campaign.name ?? campaign.campaignId}</Text>
                  {campaign.name && <Text color="secondary" type="b200">{campaign.campaignId}</Text>}
                </Column>
              </Row>

              <Row spacing="200" widths={['240px', 'fill']}>
                <Text color="secondary" type="b300">{t('form_campaigns_publisher')}</Text>
                <PublisherDisplay publisher={campaign.publisher} />
              </Row>

              <Row spacing="200" widths={['240px', 'auto']}>
                <Text color="secondary" type="b300">{t('form_global_campaign_status')}</Text>
                <Tag type={campaign.status === CampaignStatuses.ACTIVE ? 'success' : 'warning'}>
                  {campaign.status}
                </Tag>
              </Row>

              <Row spacing="200" widths={['240px', 'fill']}>
                <Text color="secondary" type="b300">{t('form_global_created_at')}</Text>
                <Text type="b300">{formatDate(campaign.createdAt)}</Text>
              </Row>

              {program?.schedule?.refreshScheduleCron && (
                <Row spacing="200" widths={['240px', 'fill']}>
                  <Text color="secondary" type="b300">{t('form_program_refresh_schedule')}</Text>
                  <Text type="b300">
                    {`${cronstrue.toString(program.schedule.refreshScheduleCron, { throwExceptionOnParseError: false, verbose: true })} (UTC)`}
                  </Text>
                </Row>
              )}

              {program?.userSharedList && program.userSharedList.length > 0 && (
                <Row spacing="200" widths={['240px', 'fill']}>
                  <Text color="secondary" type="b300">{t('program_shared_with_header')}</Text>
                  <Text type="b300">{program.userSharedList.join(', ')}</Text>
                </Row>
              )}

              {isFeatureEnabled(FEATURE_FLAGS.ENABLE_BLOCKLIST) && blocklistNames && (
                <Row spacing="200" widths={['240px', 'fill']}>
                  <Text color="secondary" type="b300">{t('form_campaigns_blocklist')}</Text>
                  <Text type="b300">{blocklistNames}</Text>
                </Row>
              )}

              {isFeatureEnabled(FEATURE_FLAGS.ENABLE_ASSET_EVAL_TRIGGER) && contentRuleNames && (
                <CampaignContentRulesRow contentRuleNames={contentRuleNames} />
              )}
            </Column>
          </Column>

          {/* Product Strategies */}
          <ProductStrategies campaign={campaign} />

          {/* Placements */}
          {campaign.placements && campaign.placements.length > 0 && (
            <Column spacing="400">
              <Text type="h200">{t('placements_header')}</Text>
              <Divider />

              <Row spacing="200" widths={['240px', 'fill']}>
                <Text color="secondary" type="b300">{t('total_placements_label')}</Text>
                <Text type="b300">{t('placements_count', { count: campaign.placements.length })}</Text>
              </Row>
            </Column>
          )}
      </Column>
      </Box>
    </div>
  );
};
